import { publish } from '../../services/utils/pubsub';
import JxFormBase from './JxFormBase';
import { JxWindowEvents } from '../JxWindowManager/JxWindowEvents';
import { clampPos } from './jxFormGeoUtils';

/**
 * JxFormDraggableBase — drag-to-move.
 * Geo itself (initial resolution: cache-or-default) is owned by JxFormBase.componentDidMount —
 * this class only ever reads/writes state.geo.top/left while dragging, never initializes it.
 */
export default class JxFormDraggableBase extends JxFormBase {
    state = {
        ...this.state,
        dragging: false,
    };

    _dragOffset = { x: 0, y: 0 };

    componentWillUnmount() {
        super.componentWillUnmount();
    }

    getTarget() {
        return this.getId();
    }

    getObjectType() {
        return this.name;
    }

    _onMouseDown = (e) => {
        if (e.button !== 0) return;
        if (e.target.closest('button')) return;
        e.preventDefault();

        this.bringToTop();
        this._dragOffset = { x: e.clientX - this.state.geo.left, y: e.clientY - this.state.geo.top };
        this.setState({ dragging: true });

        document.body.style.userSelect = 'none';
        document.body.classList.add('jx-dragging');
        document.body.style.cursor = 'grabbing';

        window.addEventListener('mousemove', this._onMouseMoveBound, true);
        window.addEventListener('mouseup', this._onMouseUpBound, true);
    };

    // Stable references for addEventListener/removeEventListener — arrow fields delegate to
    // overridable prototype methods below. Arrow class fields can't call super(), so the
    // actual logic lives in handleMouseMove/handleMouseUp (see Development.Patterns rk=7ac11d29).
    _onMouseMoveBound = (ev) => this.handleMouseMove(ev);
    _onMouseUpBound = (ev) => this.handleMouseUp(ev);

    /**
     * Updates geo.top/left in state as the pointer moves during drag.
     * Prototype method (not arrow field) so subclasses can override + call super.handleMouseMove().
     * @param {MouseEvent} ev
     */
    handleMouseMove(ev) {
        ev.stopImmediatePropagation();
        const clamped = clampPos({
            left: ev.clientX - this._dragOffset.x,
            top: ev.clientY - this._dragOffset.y,
        });
        this.setState({ geo: { ...this.state.geo, ...clamped } });
    }

    /**
     * Ends the drag — resets cursor/body state, publishes final pos, removes listeners.
     * Prototype method — subclasses overriding this MUST call super.handleMouseUp().
     */
    handleMouseUp() {
        document.body.style.userSelect = '';
        document.body.classList.remove('jx-dragging');
        document.body.style.cursor = '';
        this.setState({ dragging: false });

        this.publishWindowPos();

        window.removeEventListener('mousemove', this._onMouseMoveBound, true);
        window.removeEventListener('mouseup', this._onMouseUpBound, true);
    }

    publishWindowPos() {
        const { top, left, width, height } = this.state.geo;
        publish(JxWindowEvents.channel, {
            name: JxWindowEvents.onPosChanged,
            target: this.getTarget(),
            data: { posX: left, posY: top, width, height, component: this.getObjectType() },
        });
    }

    get dragHandleProps() {
        return {
            onMouseDown: this._onMouseDown,
            style: { cursor: this.state.dragging ? 'grabbing' : 'grab' },
        };
    }

    get panelStyle() {
        return { top: this.state.geo.top, left: this.state.geo.left, zIndex: this.state.zIndex };
    }

    get panelProps() {
        return {
            id: this.getId(),
            onMouseDown: this.bringToTop,
        };
    }
}
